import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { cloneDeep } from 'lodash'

import Modal from 'components/Modal/Modal'
import Header from 'components/Modal/components/Header/Header'
import Body from 'components/Modal/components/Body/Body'

import { ProjectContext } from 'pages/Editor/contexts/projectContext'

import StopPanels, { useStopPanes, VALIDATION_WARNING_TYPES } from '../components/StopPanels/StopPanels'

import Playground from './views/Playground/Playground'
import Sections from './views/Sections/Sections'

import { TABS, TABS_ARRAY } from './constants'
import {
    createWheelSpinBlock,
    BLOCK_KEYS,
    Playground as PlaygroundModel,
    createSections,
    BLOCK_KEYS_BY_TABS,
} from './wheelSpinService'
import { validateTabs } from './validation'

import styles from './WheelSpinModal.module.scss'

const WheelSpinModal = ({ methods, data: { structure, colorTheme, colorScheme, fontFamily, id } }) => {
    const { t } = useTranslation()
    const [tmpStructure, setTmpStructure] = useState(
        Object.keys(structure).length === 0 ? createWheelSpinBlock() : cloneDeep(structure),
    )
    const [activeTab, setActiveTab] = useState(TABS.PLAYGROUND)
    const [errors, setErrors] = useState({})

    const {
        isShowQuitAccept,
        onShowQuitAccept,
        validationWarningType,
        setValidationWarningType,
        onClosePanels,
    } = useStopPanes()

    const { isMultiplayerGame } = useContext(ProjectContext)

    useEffect(() => {
        const sectionsCount = tmpStructure[BLOCK_KEYS.PLAYGROUND][PlaygroundModel.sectionsCount]
        const sections = cloneDeep(tmpStructure[BLOCK_KEYS.SECTIONS])

        if (sectionsCount > sections.length) {
            setTmpStructure(prevState => {
                return {
                    ...prevState,
                    [BLOCK_KEYS.SECTIONS]: [
                        ...sections,
                        ...createSections(sectionsCount - sections.length, sections.length + 1),
                    ],
                }
            })
        }
        if (sectionsCount < sections.length) {
            sections.splice(sectionsCount, sections.length - sectionsCount)
            setTmpStructure(prevState => {
                return {
                    ...prevState,
                    [BLOCK_KEYS.SECTIONS]: sections,
                }
            })
        }
    }, [tmpStructure[BLOCK_KEYS.PLAYGROUND][PlaygroundModel.sectionsCount]])

    const onSave = async () => {
        try {
            const validationResult = validateTabs(tmpStructure)
            if (!validationResult.isHasError) {
                await methods.save({ [id]: { data: { struct: tmpStructure } } })
                methods.closeModal()
            } else {
                setErrors(validationResult.errors)
                setValidationWarningType(VALIDATION_WARNING_TYPES.COMMON)
            }
        } catch (err) {
            console.error(err)
        }
    }

    const onUpdatePlayground = data => {
        if (errors[BLOCK_KEYS.PLAYGROUND]) {
            const _errors = {}
            Object.keys(data).forEach(key => (_errors[key] = []))
            setErrors(prevState => ({
                ...prevState,
                [BLOCK_KEYS.PLAYGROUND]: {
                    ...prevState[BLOCK_KEYS.PLAYGROUND],
                    ..._errors,
                },
            }))
        }
        setTmpStructure(prevState => ({
            ...prevState,
            [BLOCK_KEYS.PLAYGROUND]: {
                ...prevState[BLOCK_KEYS.PLAYGROUND],
                ...data,
            },
        }))
    }
    const onUpdateSection = (sectionIndex, sectionId, data) => {
        if (errors[BLOCK_KEYS.SECTIONS]) {
            const _errors = {}
            Object.keys(data).forEach(key => (_errors[key] = []))
            const sectionsErrors = cloneDeep(errors[BLOCK_KEYS.SECTIONS])
            sectionsErrors.set(sectionId, {
                ...sectionsErrors.get(sectionId),
                ..._errors,
            })
            setErrors(prevState => ({
                ...prevState,
                [BLOCK_KEYS.SECTIONS]: sectionsErrors,
            }))
        }

        const sections = cloneDeep(tmpStructure[BLOCK_KEYS.SECTIONS])
        sections[sectionIndex] = {
            ...sections[sectionIndex],
            ...data,
        }

        setTmpStructure(prevState => ({
            ...prevState,
            [BLOCK_KEYS.SECTIONS]: sections,
        }))
    }

    const onUpdateSectionsPosition = (currentIndex, newIndex) => {
        const sections = cloneDeep(tmpStructure[BLOCK_KEYS.SECTIONS])
        const movingItem = sections[currentIndex]
        sections[currentIndex] = sections[newIndex]
        sections[newIndex] = movingItem
        setTmpStructure(prevState => ({
            ...prevState,
            [BLOCK_KEYS.SECTIONS]: sections,
        }))
    }

    const onChangeTab = useCallback(
        tabId => {
            const validationResult = validateTabs(tmpStructure, BLOCK_KEYS_BY_TABS[activeTab])
            if (!validationResult.isHasError) setActiveTab(tabId)
            else {
                setErrors(validationResult.errors)
                setValidationWarningType(VALIDATION_WARNING_TYPES.ACTIVE_TAB)
            }
        },
        [tmpStructure, activeTab],
    )

    return (
        <Modal closeOnOverlayClick={false} closeOnEsc={false}>
            <Header title={t('Edit the wheel')} onClose={onShowQuitAccept} />
            <Body
                sideNav={{
                    items: TABS_ARRAY.filter(tab => (isMultiplayerGame ? !tab.isHideFromMultiplayer : true)),
                    activeItemId: activeTab,
                    onChangeItem: onChangeTab,
                    analyticsBlockName: 'WHEEL_SPIN',
                }}
                onSave={onSave}
            >
                <div className={styles.wheelSpinModal}>
                    <div className={styles.body}>
                        {activeTab === TABS.PLAYGROUND && (
                            <Playground
                                errors={errors?.[BLOCK_KEYS.PLAYGROUND]}
                                colorTheme={colorTheme}
                                colorScheme={colorScheme}
                                fontFamily={fontFamily}
                                structure={tmpStructure}
                                onUpdatePlayground={onUpdatePlayground}
                            />
                        )}
                        {activeTab === TABS.SECTIONS && (
                            <Sections
                                errors={errors?.[BLOCK_KEYS.SECTIONS]}
                                colorTheme={colorTheme}
                                structure={tmpStructure}
                                onUpdateSection={onUpdateSection}
                                onUpdateSectionsPosition={onUpdateSectionsPosition}
                            />
                        )}
                    </div>
                </div>
            </Body>

            <StopPanels
                isShowQuitAccept={isShowQuitAccept}
                validationWarningType={validationWarningType}
                onBack={onClosePanels}
                onQuit={() => methods.closeModal()}
            />
        </Modal>
    )
}

export default WheelSpinModal
